import { NgTemplateOutlet } from '@angular/common';
import { Component, EventEmitter, inject, Input, OnChanges, Output } from '@angular/core';
import { PermissionService } from '../../../../core/services/permission.service';
import { KEYCLOAK_ROLES } from '../../../../shared/constants/auth.constants';
import {
  FinancialReconciliationSummary,
  ReconciliationChannelSummary,
} from '../../../financial-reconciliation/models/financial-reconciliation.model';
import { DashboardChannel, DashboardChannelRow } from '../../models/dashboard.model';
import { costCovered, LOW_MARGIN_PERCENT, MarginFacts, marginUnavailablePercent } from '../../utils/dashboard-actions';
import {
  channelLabel,
  count,
  FlowCell,
  FlowLink,
  flowScope,
  invoiceLink,
  orderLink,
  percent,
  reconciliationLink,
} from '../../utils/dashboard-flow';
import { currencyBRL } from '../../utils/dashboard-format';
import { DashboardFlowCellComponent } from '../dashboard-flow-cell/dashboard-flow-cell.component';

interface ChannelLine {
  id: number | null;
  name: string;
  soldAmount: number;
  invoicedAmount: number;
  orders: FlowCell;
  sold: FlowCell;
  ticket: FlowCell;
  cancelled: FlowCell;
  invoiced: FlowCell;
  contribution: FlowCell;
  /** Participação (0 a 100) pelo vendido e pelo faturado. */
  shareSold: number;
  shareInvoiced: number;
}

// Canais (spec §7): uma linha por canal, ordenada pelo vendido. Pedidos, vendido e cancelados vêm dos
// pedidos; faturado, das notas; contribuição, do resumo da conciliação por canal. Cada número abre a
// lista no mesmo recorte. Quem só tem `invoice` vê Canal, Faturado e Participação (pelo faturado): as
// colunas de pedidos e a Contribuição somem, em vez de mostrar zeros que não são reais.
@Component({
  selector: 'app-dashboard-channels',
  standalone: true,
  imports: [NgTemplateOutlet, DashboardFlowCellComponent],
  host: { class: 'col-12' },
  styleUrls: ['../dashboard-table.scss'],
  styles: [
    `
      .share {
        display: flex;
        align-items: center;
        justify-content: flex-end;
        gap: 0.5rem;
        white-space: nowrap;
        font-variant-numeric: tabular-nums;
      }

      .share-track {
        width: 4rem;
        height: 0.375rem;
        border-radius: 1rem;
        background: var(--bs-border-color-translucent, rgba(67, 89, 113, 0.1));
        overflow: hidden;
      }

      .share-bar {
        height: 100%;
        border-radius: 1rem;
        background: var(--bs-primary);
      }

      @media (max-width: 767.98px) {
        .share-track {
          width: 2.5rem;
        }
      }
    `,
  ],
  template: `
    <div class="card h-100">
      <div class="card-header">
        <h5 class="card-title mb-0">Canais</h5>
      </div>
      <div class="card-body">
        @if (empty) {
          <div class="text-center text-muted py-4" data-empty>
            <i class="ti ti-chart-bar-off d-block mb-2" style="font-size: 1.75rem; opacity: 0.5"></i>
            <p class="mb-3 small">Nenhuma venda no período.</p>
            <button type="button" class="btn btn-sm btn-label-primary" (click)="widenPeriod.emit()">
              Ver últimos 90 dias
            </button>
          </div>
        } @else {
          <div class="dash-table-wrap">
            <table class="dash-table">
              <thead>
                <tr>
                  <th scope="col">Canal</th>
                  @if (canOrder) {
                    <th scope="col" data-col="orders">Pedidos</th>
                    <th scope="col" data-col="sold">Vendido</th>
                    <th scope="col" data-col="ticket">Ticket</th>
                    <th scope="col" data-col="cancelled">Cancelados</th>
                  }
                  @if (canInvoice) {
                    <th scope="col" data-col="invoiced">Faturado</th>
                  }
                  @if (showContribution) {
                    <th scope="col" data-col="contribution">Contribuição</th>
                  }
                  <th scope="col" data-col="share">Participação</th>
                </tr>
              </thead>
              <tbody>
                @for (line of lines; track line.id) {
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: line }"></ng-container>
                }
              </tbody>
              @if (total) {
                <tfoot>
                  <ng-container *ngTemplateOutlet="row; context: { $implicit: total }"></ng-container>
                </tfoot>
              }
            </table>
          </div>
        }
      </div>
    </div>

    <ng-template #row let-line>
      <tr [attr.data-row]="line.id ?? 'total'">
        <td><span class="dash-channel-name">{{ line.name }}</span></td>
        @if (canOrder) {
          <td data-col="orders"><app-dashboard-flow-cell [cell]="line.orders"></app-dashboard-flow-cell></td>
          <td data-col="sold"><app-dashboard-flow-cell [cell]="line.sold"></app-dashboard-flow-cell></td>
          <td data-col="ticket"><app-dashboard-flow-cell [cell]="line.ticket"></app-dashboard-flow-cell></td>
          <td data-col="cancelled"><app-dashboard-flow-cell [cell]="line.cancelled"></app-dashboard-flow-cell></td>
        }
        @if (canInvoice) {
          <td data-col="invoiced"><app-dashboard-flow-cell [cell]="line.invoiced"></app-dashboard-flow-cell></td>
        }
        @if (showContribution) {
          <td data-col="contribution"><app-dashboard-flow-cell [cell]="line.contribution"></app-dashboard-flow-cell></td>
        }
        <td data-col="share">
          @if (line.id !== null) {
            @let share = canOrder ? line.shareSold : line.shareInvoiced;
            <span class="share">
              <span class="share-track"><span class="share-bar d-block" [style.width.%]="share"></span></span>
              {{ pct(share) }}
            </span>
          }
        </td>
      </tr>
    </ng-template>
  `,
})
export class DashboardChannelsComponent implements OnChanges {
  /** `channelTable` do snapshot (uma linha por canal, integrações removidas no fim). */
  @Input() rows?: DashboardChannelRow[] | null;
  @Input() channels?: DashboardChannel[] | null;
  /** Resumo da conciliação no período (já filtrado pelo canal escolhido); `byChannel` dá a contribuição. */
  @Input() summary?: FinancialReconciliationSummary | null;
  @Input() period?: { from?: string; to?: string };
  @Output() widenPeriod = new EventEmitter<void>();

  private readonly permissions = inject(PermissionService);

  private bySold: ChannelLine[] = [];
  private byInvoiced: ChannelLine[] = [];
  total: ChannelLine | null = null;

  // Getters: a permissão efetiva pode chegar (ou mudar com a empresa) depois de o card nascer.
  get canOrder(): boolean {
    return this.permissions.has(KEYCLOAK_ROLES.ORDER);
  }

  get canInvoice(): boolean {
    return this.permissions.has(KEYCLOAK_ROLES.INVOICE);
  }

  /** Contribuição só com pedidos e com o resumo por canal (API antiga não manda `byChannel`). */
  get showContribution(): boolean {
    return this.canOrder && !!this.summary?.byChannel;
  }

  /** Ordem pelo vendido; quem só vê notas tem a ordem (e a participação) pelo faturado. */
  get lines(): ChannelLine[] {
    return this.canOrder ? this.bySold : this.byInvoiced;
  }

  get empty(): boolean {
    const lines = this.lines;
    if (!lines.length) return true;
    return lines.every(
      (l) => (!this.canOrder || l.orders.value === 0) && (!this.canInvoice || l.invoicedAmount === 0),
    );
  }

  pct(share: number): string {
    return percent(Math.round(share));
  }

  ngOnChanges(): void {
    const rows = this.rows ?? [];
    const soldTotal = rows.reduce((s, r) => s + r.soldAmount, 0);
    const invoicedTotal = rows.reduce((s, r) => s + r.invoicedAmount, 0);

    const lines = rows.map((r) =>
      this.line(
        r.integrationId,
        channelLabel(r.integrationId, this.channels),
        r,
        this.summary?.byChannel?.find((c) => c.integrationId === r.integrationId) ?? null,
        { sold: soldTotal, invoiced: invoicedTotal },
      ),
    );

    this.bySold = [...lines].sort((a, b) => b.soldAmount - a.soldAmount || b.invoicedAmount - a.invoicedAmount);
    this.byInvoiced = [...lines].sort((a, b) => b.invoicedAmount - a.invoicedAmount || b.soldAmount - a.soldAmount);

    this.total =
      rows.length >= 2
        ? this.line(
            null,
            'Total',
            {
              integrationId: 0,
              orders: rows.reduce((s, r) => s + r.orders, 0),
              soldAmount: soldTotal,
              cancelled: rows.reduce((s, r) => s + r.cancelled, 0),
              invoicedAmount: invoicedTotal,
            },
            this.summary ?? null,
            { sold: soldTotal, invoiced: invoicedTotal },
          )
        : null;
  }

  private line(
    id: number | null,
    name: string,
    r: DashboardChannelRow,
    margin: ReconciliationChannelSummary | FinancialReconciliationSummary | null,
    totals: { sold: number; invoiced: number },
  ): ChannelLine {
    // Linha de canal leva o canal; a de total, todos (sem integrationId).
    const scope = flowScope(this.period, id !== null ? [id] : null);
    const valid = r.orders - r.cancelled;
    const cell = (value: number, link: FlowLink | null, text?: string): FlowCell => ({ value, text, link, notes: [] });

    return {
      id,
      name,
      soldAmount: r.soldAmount,
      invoicedAmount: r.invoicedAmount,
      orders: cell(r.orders, orderLink('received', scope)),
      sold: cell(r.soldAmount, orderLink('sold', scope), currencyBRL(r.soldAmount)),
      // Ticket é razão, não um passo: sem lista própria, sem link.
      ticket: cell(0, null, currencyBRL(valid > 0 ? r.soldAmount / valid : 0)),
      cancelled: cell(
        r.cancelled,
        orderLink('cancelled', scope),
        r.cancelled > 0 && r.orders > 0
          ? `${count(r.cancelled)} (${percent(Math.round((r.cancelled / r.orders) * 1000) / 10)})`
          : count(r.cancelled),
      ),
      invoiced: cell(r.invoicedAmount, invoiceLink('revenue', scope), currencyBRL(r.invoicedAmount)),
      contribution: this.contribution(margin, r.invoicedAmount, reconciliationLink('contribution', scope)),
      shareSold: totals.sold > 0 ? (r.soldAmount / totals.sold) * 100 : 0,
      shareInvoiced: totals.invoiced > 0 ? (r.invoicedAmount / totals.invoiced) * 100 : 0,
    };
  }

  // Mesma regra do card Contribuição: margem indisponível antes de tudo; contribuição só com custo
  // em 80% ou mais das vendas; senão o resultado antes do custo.
  private contribution(facts: MarginFacts | null, invoiced: number, link: FlowLink): FlowCell {
    // Sem linha deste canal no resumo (ele não vendeu no período): não há margem para mostrar, e um
    // "R$ 0,00" seria um número inventado.
    if (!facts) return { value: null, link: null, notes: [] };

    const unlinked = marginUnavailablePercent(facts, invoiced);
    if (unlinked !== null) {
      return {
        value: 0,
        text: 'Indisponível',
        link,
        tone: 'muted',
        // Motivo escrito na célula, não em tooltip: no toque não há hover para abri-lo.
        notes: [{ text: `Margem indisponível: ${unlinked}% das notas sem pedido vinculado`, link: null, tone: 'muted' }],
      };
    }

    const m = facts as ReconciliationChannelSummary;
    const covered = costCovered(m);
    const value = covered ? m.costedMargin.cleanValue : (m.margin?.preCostResult ?? 0);
    const pct = covered ? m.costedMargin.cleanValuePercent : m.margin?.preCostPercent;
    const hasPct = pct !== null && pct !== undefined;
    const label = covered
      ? hasPct ? `${percent(pct)} da venda` : null
      : hasPct ? `${percent(pct)} antes do custo` : 'Antes do custo';

    return {
      value,
      text: currencyBRL(value),
      link,
      tone: value < 0 || (covered && hasPct && pct < LOW_MARGIN_PERCENT) ? 'danger' : null,
      notes: label ? [{ text: label, link: null, tone: 'muted' }] : [],
    };
  }
}
